// src/utils/launchData.js
// Starts the app's two big downloads (every deal, and the team list) as soon as the
// signed-in person's own record has come back and the account is let in, and lets the
// first screens that open reuse them.
//
// Why: those downloads used to wait for the dashboard's code to arrive and draw
// before they even began. And sharedRead.js
// only joins a download that is still running, so a screen whose code arrived after
// the dashboard's download had finished (the first open after an update) pulled
// every deal a second time.
//
// Safety rules, each pinned by a test (utils/__tests__/launchData.test.js):
//  - Started only from App.js, the moment sign-in is confirmed, never before (a read
//    before sign-in once locked the whole team out). They no longer hold that person's
//    own record back: it is read on a quick connection of its own (utils/fastUserRead.js),
//    after sharing one with these downloads kept "Loading..." up for the whole 30 MB of
//    deals (09/15/2026).
//  - Tied to that person: never handed to anyone else, and thrown away on sign-out,
//    on a deleted or disabled account, or when their record cannot be loaded.
//  - Each screen reuses it once. Opening that screen again downloads fresh.
//  - Reused only while the app is opening (LAUNCH_WINDOW_MS), and once the download
//    has finished, only for a few seconds more (LAUNCH_REUSE_AFTER_FINISH_MS): long
//    enough for a screen whose code arrives just after it, too short for anyone to
//    have opened a screen and changed something in between.
//  - Every deal save retires the deals copy just before it writes (firebasefunctions.js
//    and every file that writes a deal directly, pinned by
//    dealWritesRetireLaunchCopy.test.js). saveitemwithid and updateItem retire the
//    team list too, and a fresh read retires either. Nothing older than a save is
//    ever handed out.
//  - A failed download is never handed out again.
//
// Pure: no Firebase here. firebasefunctions.js supplies the actual reads.

export const LAUNCH_WINDOW_MS = 30000;
export const LAUNCH_REUSE_AFTER_FINISH_MS = 10000;

let launch = null; // { uid, startedAt, reads: Map<name, { promise, failed, finishedAt, claimedBy: Set }> }

export function startLaunchData(uid, readers, { now = Date.now } = {}) {
  if (!uid) {
    launch = null;
    return;
  }
  const reads = new Map();
  Object.entries(readers || {}).forEach(([name, read]) => {
    const entry = { promise: null, failed: false, finishedAt: null, claimedBy: new Set() };
    try {
      entry.promise = Promise.resolve(read());
    } catch (err) {
      entry.promise = Promise.reject(err);
    }
    // Notes when the download finished, and marks a failure so it is never handed out
    // again. Handling the failure here also keeps an unclaimed failure from surfacing
    // as an unhandled rejection; a screen that already took the download still
    // receives the error on its own copy.
    entry.promise.then(
      () => {
        entry.finishedAt = now();
      },
      () => {
        entry.failed = true;
      },
    );
    reads.set(name, entry);
  });
  launch = { uid, startedAt: now(), reads };
}

// The download started at sign-in, or null when this screen must read for itself.
export function claimLaunchRead(name, { uid, consumer, now = Date.now } = {}) {
  if (!launch || !uid || !consumer || launch.uid !== uid) return null;
  if (now() - launch.startedAt >= LAUNCH_WINDOW_MS) return null;
  const entry = launch.reads.get(name);
  if (!entry || entry.failed || entry.claimedBy.has(consumer)) return null;
  if (entry.finishedAt !== null && now() - entry.finishedAt >= LAUNCH_REUSE_AFTER_FINISH_MS) {
    return null;
  }
  entry.claimedBy.add(consumer);
  return entry.promise;
}

// Called on a save or a refresh of that data: the next screen reads fresh.
export function retireLaunchRead(name) {
  if (launch) launch.reads.delete(name);
}

// Sign-out, a refused account, or a failed user lookup.
export function clearLaunchData() {
  launch = null;
}
